import React, { useState, useEffect } from 'react';
import TypingText from '../components/TypingText';
import ProfileCard from '../components/ProfileCard';
import ProjectCard from '../components/ProjectCard';
import BackgroundLine, { Functions } from '../components/BackgroundLine';
import GaussianProcessDemo from '../components/GaussianProcessDemo';
import RLGridWorld from '../components/RLGridWorld';
import RecommendationDemo from '../components/RecommendationDemo';
import MultiArmedBanditDemo from '../components/MultiArmedBanditDemo';
import Tex2SVG from "react-hook-mathjax";

function HomePage({ personalInfo, links }) {
  const [showTyping, setShowTyping] = useState(false);
  const [typingComplete, setTypingComplete] = useState(false);
  
  // Add animation timing effect
  useEffect(() => {
    // Wait for profile card animation before showing typing
    const timer = setTimeout(() => {
      setShowTyping(true);
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Function to handle when typing is complete
  const handleTypingComplete = () => {
    setTypingComplete(true);
  };
  

  return (
    <>
      <header className="visually-hidden">
        <h1>Julian Streibel - Machine Learning Engineer</h1>
      </header>
      {/* Class Likelihoods - P(X|C) */}
      <BackgroundLine 
        yOffset={90} 
        functionType={Functions.class1Likelihood} 
        amplitude={60} 
        strokeColor="rgba(255, 0, 0, 0.52)" 
      />
      <BackgroundLine 
        yOffset={90} 
        functionType={Functions.class2Likelihood} 
        amplitude={60}
        strokeColor="rgba(0, 0, 255, 0.52)" 
      />

      {/* Marginal Distribution P(X) */}
      <BackgroundLine 
        yOffset={180} 
        functionType={Functions.marginalX} 
        amplitude={60} 
        strokeColor="rgba(123, 0, 123, 0.52)" 
      />

      {/* Posterior Distributions - P(C|X) */}
      <BackgroundLine 
        yOffset={270} 
        functionType={Functions.posteriorClass1} 
        amplitude={60} 
        strokeColor="rgba(255, 0, 0, 0.52)" 
      />
      <BackgroundLine 
        yOffset={270} 
        functionType={Functions.posteriorClass2} 
        amplitude={60} 
        strokeColor="rgba(0, 0, 255, 0.52)" 
      />

      {/* Add Distribution Labels */}
      <div className="distribution-labels">
        <div className="label" style={{
          top: "100px", 
          left: "98%", 
          transform: "translateX(-100%)",
          opacity: 0.5,
          fontSize: "12px",
          backgroundColor: "transparent",
          color: "rgba(0, 0, 0, 0.5)",
          padding: "2px 4px"
        }}>P(X|C)</div>
        <div className="label" style={{
          top: "190px", 
          left: "98%", 
          transform: "translateX(-100%)",
          opacity: 0.5,
          fontSize: "12px",
          backgroundColor: "transparent",
          color: "rgba(0, 0, 0, 0.5)",
          padding: "2px 4px"
        }}>P(X)</div>
        <div className="label" style={{
          top: "280px", 
          left: "98%", 
          transform: "translateX(-100%)",
          opacity: 0.5,
          fontSize: "12px",
          backgroundColor: "transparent",
          color: "rgba(0, 0, 0, 0.5)",
          padding: "2px 4px"
        }}>P(C|X)</div>
      </div>

      <main>
        <div className="content-section">
          <section aria-labelledby="about-section">
            <h2 id="about-section" className="visually-hidden">About Julian Streibel</h2>
            <ProfileCard 
              image={personalInfo.image}
              name={personalInfo.name}
              birthdate={personalInfo.birthdate}
              location={personalInfo.location}
              company={personalInfo.company}
              position={personalInfo.position}
              education={personalInfo.education}
              links={links}
            />
            
            {showTyping && (
              <div className="typing-section">
                <TypingText 
                  text="Hi, I'm Julian 👋\nI'm working on: \n- 🤖 Machine Learning\n- 🏗️ Data Engineering\n- 🚀 ML Ops\n- 🌐 Full Stack\n\nExplore my projects on GitHub and connect with me on LinkedIn!"
                  baseSpeed={35}
                  onComplete={handleTypingComplete}
                  skipTyping={typingComplete}
                />
              </div>
            )}
            
            {typingComplete && (
              <>
                <ProjectCard 
                  title="Machine Learning Engineer & Data Scientist"
                  paragraphs={[
                    "Currently building ML systems at a stealth startup where I work on both modeling and infrastructure - developing ETL pipelines, designing data warehouse solutions, and implementing production-ready machine learning models.",
                    "Previously worked as a Data Scientist at Merck Group, focusing on commercial applications including product recommendations and Bayesian optimization. Contributed to BayBE (Bayesian Optimization for Experiments) while working in the Analytics Center of Excellence.",
                    "Complemented my data science expertise with experience as a self-employed Full Stack Developer, where I architected and delivered complete web applications from concept to deployment. This multi-disciplinary background allows me to bridge the gap between sophisticated data systems and intuitive, user-friendly interfaces.",
                    "My research includes a publication on identifying user innovations through AI in online communities using transfer learning approaches."
                  ]}
                  stats={[
                    { value: "5+", label: "Years Experience" },
                    { value: "3", label: "Industry Sectors" },
                    { value: "10+", label: "ML Systems Deployed" }
                  ]}
                />

                {/* MAB Demo */}
                <ProjectCard 
                  title="Multi-Armed Bandit"
                  paragraphs={[
                    "This interactive demo explores the multi-armed bandit problem, a fundamental challenge in decision-making under uncertainty. Imagine you're faced with several slot machines (bandits), each with unknown reward probabilities. Your goal is to maximize cumulative rewards by balancing exploration (learning about machines) with exploitation (choosing the best machine).",
                    "Adjust the win rates for each action using the sliders, then click 'Start Optimization' to see different algorithms in action. The demo implements two strategies: Thompson Sampling, which makes decisions by sampling from posterior distributions, and Posterior Standard Deviation, which prioritizes exploration of uncertain options.",
                    "Behind the scenes: The problem uses a Bayesian framework where each action's reward follows a Bernoulli distribution (success/failure) with unknown parameter θ (win rate). We use a Beta distribution as our prior belief about θ.",
                    <MultiArmedBanditDemo key="mab-demo" />,
                    "The visualizations show the posterior distributions (our beliefs about each action's success probability), the cumulative regret (reward lost compared to the optimal action), and the probability that each action is optimal based on current evidence.",
                    "Multi-armed bandits find applications in online advertising, clinical trials, and recommendation systems - anywhere we need to make sequential decisions while learning from limited feedback."
                  ]}
                />

                {/* New Recommendation Demo */}
                <ProjectCard 
                  title="Preference Learning"
                  paragraphs={[
                    "In preference learning, we try to represent preferences of users and use them to make recommendations. This example is learning a three dimensional embedding space with euclidean distance d as a distance metric. We are using triplets to learn this embedding space with a global structure. Triplet loss looks like this:",
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', maxWidth: '100%'}}>
                      <Tex2SVG display="inline" latex="L = max(0, m + d(anchor, negative) - d(anchor, positive) )" />
                    </div>,
                    "It uses the user as an anchor and tries to ensure that the distance between the anchor and positive is smaller than the distance between the anchor and negative by a margin m. This is forming a global structure of the embedding space, where we do not see local collapse as the optimization progresses. To learn meaningful representations normally a large dataset is needed.",
                    <RecommendationDemo key="recommendation-demo" />,
                    "You can add interests to users and let them give positive or negative feedback. The algorithm will then try to find the best embedding space for the users and products and show the next best and worst interests for the users. You can interact and explore the embedding space."
                  ]}
                />
   
    
                
                {/* Interactive Gaussian Process Visualization */}
                <ProjectCard 
                  title="Interactive Bayesian Optimization"
                  paragraphs={[
                    "This interactive demo showcases Bayesian Optimization with Gaussian Processes, a powerful approach for optimizing expensive-to-evaluate black-box functions. Draw any function on the canvas by clicking and dragging, then click 'Start Optimization' to watch the algorithm search for the maximum value.",
                    <GaussianProcessDemo key="gp-demo" />,
                    "How it works: The demo uses a Gaussian Process with a Matérn kernel, with hyperparameters that are automatically optimized based on observed data. The bright blue line represents the model's prediction, while the light blue shaded area shows the uncertainty (±2σ confidence interval).",
                    "For deciding where to sample next, the algorithm uses q-Log Expected Improvement (qLogEI) as the acquisition function (shown in green), which provides better numerical stability than standard EI when evaluating points with small improvement probabilities. The implementation also employs an epsilon-greedy approach (10% random exploration) to escape local optima and improve global search performance.",
                    "Bayesian Optimization excels in scenarios with limited evaluation budgets, like hyperparameter tuning, drug discovery, materials science, and A/B testing. The ability to intelligently guide sampling makes it far more efficient than random or grid search methods."
                  ]}
                />
                
                {/* New RL Grid World Demo */}
                <ProjectCard 
                  title="Reinforcement Learning Grid World"
                  paragraphs={[
                    "Explore how AI agents learn to navigate environments through this interactive reinforcement learning demonstration. Design your own maze-like world by clicking cells to create empty spaces, walls (black), and rewards (gold). Watch as the agent discovers the optimal path from any starting position to the reward!",
                    <RLGridWorld key="rl-grid-demo" />,
                    "Behind the scenes: This demo implements Value Iteration, a fundamental reinforcement learning algorithm. The agent learns by calculating the 'value' of being in each position and determines the best action to take from anywhere in the grid. The brightness of green cells indicates their value – brighter means more desirable – while arrows show the agent's planned direction of movement.",
                    "The learning happens in two phases: First, the algorithm calculates values and policies during the 'optimizing' phase. Once converged, the red agent follows this policy in the 'moving' phase. Try creating different layouts with multiple rewards and obstacles to see how the agent adapts!",
                  ]}
                />
              </>
            )}
          </section>
        </div>
      </main>
    </>
  );
}

export default HomePage; 